// Hooks
import { useEffect, useState, useCallback } from "react";
import { useMedplum } from "@medplum/react";

// Types
import type { Flag, Patient } from "@medplum/fhirtypes";

// Utils
import {
  getPatientPauseStatus,
  getPauseReason,
  getRemainingPauseDays,
} from "@benchmark/core/resolvers";
import { fetchExistingActiveFlags } from "@benchmark/core/services";

// Events
import { DomainEventNames } from "@benchmark/core/events";
import { useEventDrivenRefetch } from "@benchmark/core/hooks";

/**
 * Result type for usePatientPauseStatus hook.
 * @agentic connections
 */
export type UsePatientPauseStatusResult = {
  isPaused: boolean;
  pauseFlag: Flag | null;
  pauseReason: string | undefined;
  remainingDays: number;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
};

/**
 * Fetches pause Flag for patient and derives isPaused, reason, remainingDays.
 * @agentic connections
 */
export function usePatientPauseStatus(
  patient: Patient | undefined,
): UsePatientPauseStatusResult {
  const medplum = useMedplum();
  const [pauseFlag, setPauseFlag] = useState<Flag | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchPauseStatus = useCallback(async () => {
    if (!patient) {
      setPauseFlag(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const activeFlags = await fetchExistingActiveFlags(medplum, patient.id!);
      const flag = getPatientPauseStatus(activeFlags);
      setPauseFlag(flag);
    } catch (err) {
      console.error("Error fetching pause status:", err);
      setError(err as Error);
      setPauseFlag(null);
    } finally {
      setLoading(false);
    }
  }, [patient, medplum]);

  useEffect(() => {
    let cancelled = false;

    queueMicrotask(() => {
      void fetchPauseStatus().catch((err) => {
        if (!cancelled) {
          console.error("Error in fetchPauseStatus effect:", err);
        }
      });
    });

    return () => {
      cancelled = true;
    };
  }, [fetchPauseStatus]);

  // Auto-refetch when this patient is paused/unpaused or a flag changes.
  // This makes the page-level subscription in PatientPage redundant
  // (removed in Phase 4).
  useEventDrivenRefetch(
    [
      DomainEventNames.PatientPaused,
      DomainEventNames.PatientUnpaused,
      DomainEventNames.FlagCreated,
      DomainEventNames.FlagUpdated,
    ],
    {
      refetch: fetchPauseStatus,
      enabled: !!patient,
      filter: (payload) =>
        (payload as { patientId?: string }).patientId === patient?.id,
    },
  );

  // Derived values
  const isPaused = pauseFlag !== null;
  const pauseReason = pauseFlag ? getPauseReason(pauseFlag) : undefined;
  const remainingDays = pauseFlag ? getRemainingPauseDays(pauseFlag) : 0;

  return {
    isPaused,
    pauseFlag,
    pauseReason,
    remainingDays,
    loading,
    error,
    refetch: fetchPauseStatus,
  };
}
